import { DotLottie } from '@lottiefiles/dotlottie-web';
import Cookies from 'js-cookie';
import { throttle } from 'throttle-debounce';

const animations = document.querySelectorAll('.wp-block-base-lottie-animation');
const dotLottieMap = new Map();

// Observer for animations that are in the viewport
const observer = new IntersectionObserver(
    (entries, observer) => {
        entries.forEach((entry) => {
            const dotLottie = dotLottieMap.get(entry.target);

            if (!dotLottie || (dotLottie.loop === false && dotLottie.currentFrame === dotLottie.totalFrames)) {
                observer.unobserve(entry.target);
                return;
            }

            if (entry.isIntersecting) {
                dotLottie.play();
            } else {
                dotLottie.pause();
            }
        });
    },
    {
        threshold: 0.1,
    }
);

// Handle logic for cookies to check if the animation has been played once
const getPlayedAnimations = () => {
    const playedAnimations = Cookies.get('base-lottie-animation-played-once');
    return playedAnimations ? JSON.parse(playedAnimations) : [];
};

const hasPlayedOnce = (canvas) => {
    const uniqueId = canvas.dataset.uniqueId;
    if (!uniqueId) return false;

    const playedAnimations = getPlayedAnimations();
    return playedAnimations.includes(uniqueId);
};

const markAnimationAsPlayed = (canvas) => {
    const uniqueId = canvas.dataset.uniqueId;
    if (!uniqueId) return;

    const playedAnimations = getPlayedAnimations();
    if (!playedAnimations.includes(uniqueId)) {
        playedAnimations.push(uniqueId);
        Cookies.set('base-lottie-animation-played-once', JSON.stringify(playedAnimations), { expires: 365 });
    }
};

// Setup the animations
document.addEventListener('DOMContentLoaded', () => {
    animations.forEach((animation) => {
        const canvas = animation.querySelector('canvas');

        // If any required elements are missing exit early
        if (!canvas || !canvas.dataset.animationPath) {
            return;
        }

        // Initialize lottie
        const dotLottie = new DotLottie({
            autoplay: canvas.dataset.autoplay === 'true',
            loop: canvas.dataset.loop === 'true',
            canvas: canvas,
            src: canvas.dataset.animationPath,
            renderConfig: {
                freezeOnOffscreen: canvas.dataset.playInViewport === 'true' ? false : true,
            },
        });
        dotLottieMap.set(animation, dotLottie);

        // After load setup for sizing and extra logic
        dotLottie.addEventListener('load', () => {
            canvas.classList.add('has-loaded-animation');

            // If the animation has been played once, set the frame to the last frame to trigger completion
            if (canvas.dataset.playOnce === 'true' && hasPlayedOnce(canvas)) {
                dotLottie.setFrame(dotLottie.totalFrames - 1);
            }

            // Add canvas to observer if it is set to play in viewport
            if (canvas.dataset.playInViewport === 'true') {
                observer.observe(animation);
            }
        });

        // Handle events for the animation
        dotLottie.addEventListener('play', () => {
            canvas.classList.add('has-playing-animation');
        });

        dotLottie.addEventListener('pause', () => {
            canvas.classList.remove('has-playing-animation');
        });

        dotLottie.addEventListener('stop', () => {
            canvas.classList.remove('has-playing-animation');
        });

        dotLottie.addEventListener('complete', () => {
            const event = new Event('animationComplete');
            canvas.dispatchEvent(event);

            if (canvas.dataset.playOnce === 'true') {
                markAnimationAsPlayed(canvas);
            }

            canvas.classList.remove('has-playing-animation');
            canvas.classList.add('has-completed-animation');
        });

        dotLottie.addEventListener('loop', () => {
            const event = new Event('animationLoop');
            canvas.dispatchEvent(event);

            if (!canvas.classList.contains('has-looped-animation')) {
                canvas.classList.add('has-looped-animation');
            }
        });
    });

    // Trigger dotLottie resize function on window resizes to avoid blurry canvas content
    window.addEventListener(
        'resize',
        throttle(100, () => {
            animations.forEach((animation) => {
                const dotLottie = dotLottieMap.get(animation);
                dotLottie.resize();
            });
        })
    );
});
